import { useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import PropTypes from "prop-types";

import ngDeps from "ng-react-directives/ngr-injector";
import StatusChange from "components/Collaboration/StatusChange/StatusChange";
import PostLink from "components/Posts/Post/PostLink/PostLink";
import { PageName, PAGES } from "constants/Pages";
import { sendPostNotification } from "actions/postsView";
import { getPosts } from "selectors/postsPageSelectors";
import { unreadCommentsSelector } from "selectors/collaborationSelectors";
import { markCollaborationChannelAsRead } from "actions/collaboration";
import { extractCorePost, getPageVerbs } from "utils/posts";
import { getUserDefaultStatusKey } from "utils/user";
import { showPreviewPostModal, showSkedUrlModal } from "actions/modal";
import { openEditPostModal, openCreatePostModal, Post } from "features/post";
import { useTrackEvent } from "libs/analytics";
import { isInstagramAccount } from "entities/accounts";

import "./PostActions.less";
import {
  CLICKED_PREVIEW_POST_MODAL,
  COPY_TO_DRAFT_SELECT,
} from "constants/Events";
import { entitlementFeatureToggle } from "@/utils/feature-toggle-utils";
import {
  openCreatePostModalV2,
  openEditPostModalV2,
  requestPostsFromClusterId,
} from "../../../../features/post/bulk-create/bulk-create-post.actions";
import { LoadedDraft } from "../../../../selectors/loaded-posts-normalizer";
import { AccountType } from "../../../../shared/types/accounts";
import { UserWithFuncs } from "../../../../features/user/types";

const PostActions = ({
  post,
  accounts,
  page,
  user,
  channelId,
  showText = true,
}: {
  post: Post | LoadedDraft;
  accounts: AccountType[];
  page: PageName;
  user: UserWithFuncs;
  channelId: string;
  showText?: boolean;
}) => {
  const normalizedPost = extractCorePost(post);
  const dispatch = useDispatch();
  const trackEvent = useTrackEvent();
  const { $rootScope } = ngDeps;
  const pageVerbs = getPageVerbs(page, normalizedPost, user);
  const needToOpenSuccessEditPostModal = normalizedPost.isSuccess;
  const editPostModalSuccessProps = {
    postType: "schedule", // to show when posted
    past: true, // to disable all inputs
    isPotentialError: false, // to disable all inputs
  } as const;

  const pageItemName = useMemo(() => {
    switch (page) {
      case PAGES.ARCHIVE: {
        return "View Post";
      }

      case PAGES.DRAFT: {
        return "Edit Draft";
      }

      default: {
        return "Edit Post";
      }
    }
  }, [page]);

  const posts = useSelector(getPosts);

  const unreads =
    useSelector(unreadCommentsSelector)?.find(
      (channel) => channel.channelId === channelId
    )?.unreads || 0;

  const accountMap = $rootScope.accountsMap[normalizedPost.accountId];

  const isInsta = isInstagramAccount(accountMap);
  const showPostStatus =
    user && user.isPostStatusEnabledForUser() && page !== PAGES.ARCHIVE;

  const dispatchShowSkedUrlModal = () =>
    dispatch(
      showSkedUrlModal(normalizedPost, Boolean(accountMap.accountPublicKey))
    );

  const copyToDraft = () => {
    trackEvent({ eventName: COPY_TO_DRAFT_SELECT });
    const post: Post = {
      ...normalizedPost,
      isSuccess: false,
      postStatusKey: getUserDefaultStatusKey(user),
    };
    if (entitlementFeatureToggle.get("bulk-create")) {
      dispatch(openCreatePostModalV2({ post, media: { type: "skip-media" } }));
      return;
    }

    dispatch(openCreatePostModal({ post }));
  };

  const postAgain = () => {
    const post = {
      ...normalizedPost,
      postStatusKey: getUserDefaultStatusKey(user),
    };
    if (entitlementFeatureToggle.get("bulk-create")) {
      dispatch(openEditPostModalV2({ post, modalTitle: "Post Again" }));
      return;
    }

    dispatch(openEditPostModal({ post }));
  };

  return (
    <div className="PostActions">
      {showPostStatus && (
        <div
          className={`ListPostDetails-Collab border-0 border-b border-solid border-gray-100 pb-6 mb-4`}
        >
          <StatusChange user={user} post={normalizedPost} page={page} />
        </div>
      )}
      <ul className={`PostActions-Ul list-unstyled`}>
        {pageVerbs.previewable && (
          <li>
            <PostLink
              action={() => {
                trackEvent({ eventName: CLICKED_PREVIEW_POST_MODAL });
                dispatch(showPreviewPostModal(normalizedPost, accounts, posts));
              }}
              icon="icon-eye"
              actionText="Preview"
              showText={showText}
            />
          </li>
        )}
        {pageVerbs.notifiable && (
          <li>
            <PostLink
              action={() => dispatch(sendPostNotification(normalizedPost._id))}
              icon="icon-eye"
              actionText="Send post notification"
              showText={showText}
            />
          </li>
        )}

        {pageVerbs.duplicatable && (
          <li>
            <PostLink
              action={copyToDraft}
              icon="icon-retry"
              actionText="Copy to drafts"
              showText={showText}
            />
          </li>
        )}

        {pageVerbs.repostable && (
          <li>
            <PostLink
              action={postAgain}
              icon="icon-retry"
              actionText="Post Again"
              showText={showText}
            />
          </li>
        )}
        {(pageVerbs.editable || needToOpenSuccessEditPostModal) && (
          <li>
            <PostLink
              action={() => {
                dispatch(markCollaborationChannelAsRead(channelId));
                const editPost: Post = needToOpenSuccessEditPostModal
                  ? {
                      ...normalizedPost,
                      ...editPostModalSuccessProps,
                    }
                  : normalizedPost;
                if (entitlementFeatureToggle.get("bulk-create")) {
                  if (needToOpenSuccessEditPostModal) {
                    dispatch(openEditPostModalV2({ post: editPost }));
                  } else {
                    dispatch(
                      requestPostsFromClusterId({
                        requestedPost: editPost,
                        trackEvent,
                      })
                    );
                  }
                } else {
                  dispatch(openEditPostModal({ post: editPost }));
                }
              }}
              icon="icon-pencil"
              actionText={pageItemName}
              showText={showText}
              notifyCount={unreads}
            />
          </li>
        )}
        {isInsta && pageVerbs.urlEditable && (
          <li>
            <PostLink
              action={dispatchShowSkedUrlModal}
              icon="icon-link"
              actionText={`${normalizedPost.url ? "Update" : "Add"} URL`}
              showText={showText}
            />
          </li>
        )}
      </ul>
    </div>
  );
};

PostActions.propTypes = {
  actions: PropTypes.object,
  accounts: PropTypes.array,
  post: PropTypes.object,
  page: PropTypes.string,
  user: PropTypes.object,
  showText: PropTypes.string,
  channelId: PropTypes.string,
};

export default PostActions;
